import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { downloadFile, urlToBase64 } from '@/utils';
import {
  FileTypes,
  FileUploadStatus,
  PointOfInterest,
  useRemoveAttachmentsFromPoiMutation,
  useUpdatePointOfInterestFileMutation,
  AllPointsOfInterestForInspectionDataLoaderDocument,
  ImageType,
} from '@/__generated__/graphql';
import { Attachment } from '../types';
import { MediaViewer } from '@/components/shared/MediaViewer';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { FileDisplay } from '@/components/Analysis/shared/MediaUploadsAndViewer/FileDisplay';
import { usePoiAttachmentCaption } from '@/hooks/usePoiAttachmentCaption';

type Props = {
  selectedPoiInfo: PointOfInterest;
  attachments: Attachment[];
  allAttachments?: Attachment[];
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  location?: string;
};

export const AttachmentsViewer = ({
  attachments = [],
  allAttachments,
  setAttachments,
  selectedPoiInfo,
  setIsUpdating,
  location = '',
}: Props) => {
  const [updatePoiFile] = useUpdatePointOfInterestFileMutation();
  const { updatePoiAttachmentCaption } = usePoiAttachmentCaption();
  const [removeAttachmentsFromPoi] = useRemoveAttachmentsFromPoiMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [validAttachments, setValidAttachments] = useState<Attachment[]>([]);

  useEffect(() => {
    setValidAttachments(attachments);
  }, [attachments]);

  useEffect(() => {
    (async function getImageURLs() {
      if (selectedPoiInfo.files && selectedPoiInfo.files.length > 0) {
        try {
          const validFiles = selectedPoiInfo.files.filter(
            (file) =>
              file.uploadStatus &&
              [FileUploadStatus.Success, FileUploadStatus.Inprogress].includes(file.uploadStatus)
          );

          if (validFiles.length === 0) return;

          const fetchedImagesPromises = validFiles.map(
            async ({
              fileName,
              resourcePath,
              type,
              id,
              uploadStatus,
              caption,
              imageType,
              uuid,
            }) => {
              if (!resourcePath) {
                console.warn(`Missing resourcePath for file: ${fileName}`);
                return null;
              }

              const poiAttachmentUrl = getCloudfrontUrl(resourcePath);
              const isImage =
                type.includes('image/') ||
                [FileTypes.Jpeg, FileTypes.Png].includes(type as FileTypes);

              let base64;
              try {
                base64 = isImage ? await urlToBase64(poiAttachmentUrl) : undefined;
              } catch (error) {
                console.error(`Failed to convert ${fileName} to base64:`, error);
              }

              return {
                id: id || `attachment-${fileName}-${Date.now()}`, // Ensure we always have an id
                name: fileName,
                base64: base64,
                type,
                isNew: false,
                caption: caption || undefined,
                url: poiAttachmentUrl,
                uploadStatus: uploadStatus || FileUploadStatus.Success,
                uuid,
                imageType,
              };
            }
          );

          const fetchedImages = await Promise.all(fetchedImagesPromises);
          const validFetchedImages = fetchedImages.filter(Boolean) as Attachment[];

          if (validFetchedImages.length > 0) {
            setAttachments(validFetchedImages);
          }
        } catch (error) {
          console.error('Error in getImageURLs:', error);
        }
      } else {
        setAttachments([]);
      }
    })();
  }, [selectedPoiInfo.files, setAttachments, selectedPoiInfo.id]);

  const onUploadComplete = (index: number, file: Attachment) => {
    if (file.isNew && file.resourcePath) {
      const poiAttachmentUrl = getCloudfrontUrl(file.resourcePath);
      setAttachments((previous) => {
        const newAttachments = [...previous];
        newAttachments[index] = {
          ...newAttachments[index],
          url: poiAttachmentUrl,
          type: file.type,
          isNew: false,
          uploadStatus: FileUploadStatus.Success,
        };
        return newAttachments;
      });
      if (setIsUpdating) setIsUpdating(false);
    }
  };

  const handleDownloadAttachment = (attachmentIndex: number) => {
    const currentAttachment = attachments[attachmentIndex];
    if (!currentAttachment.url) return;
    downloadFile(currentAttachment.url, currentAttachment.name);
  };

  const handleRemoveAttachment = (attachmentIndex: number) => {
    const currentAttachment = attachments[attachmentIndex];
    if (!currentAttachment.id) return;
    const attachmentsToRemove: string[] = [];
    attachmentsToRemove.push(currentAttachment.id);
    const otherAttachmentsToRemove = allAttachments?.find(
      (eachAttachment) =>
        eachAttachment.id !== currentAttachment.id &&
        eachAttachment.uuid === `${currentAttachment.uuid}-${ImageType.WhiteBgPdf}` &&
        eachAttachment.imageType === ImageType.WhiteBgPdf
    );
    if (otherAttachmentsToRemove?.id) attachmentsToRemove.push(otherAttachmentsToRemove.id);
    removeAttachmentsFromPoi({
      variables: {
        input: {
          pointOfInterestId: selectedPoiInfo.id,
          fileIds: attachmentsToRemove,
        },
      },
      refetchQueries: [AllPointsOfInterestForInspectionDataLoaderDocument],
    });

    setAttachments(attachments.filter((attachment) => attachment.id !== currentAttachment.id));
  };

  const handelPoiAttachmentUpload = async (fileId: string | undefined) => {
    if (!fileId) {
      console.warn('Attempted to upload attachment with undefined fileId');
      return;
    }
    try {
      await updatePoiFile({
        variables: {
          input: {
            fileId,
            pointOfInterestId: selectedPoiInfo.id,
            uploadStatus: FileUploadStatus.Success,
          },
        },
        onError: (error) => {
          showSnackBar(error.message, 'error');
        },
      });
    } catch (error) {
      console.error('Error uploading attachment:', error);
      showSnackBar('Failed to update attachment status', 'error');
    }
  };

  const handelPoiImageCaption = async (
    updatedCaption: string,
    fileId: string | undefined,
    uuid: string | undefined
  ) => {
    if (!fileId || !uuid) {
      console.warn('Cannot update caption: fileId or uuid is missing.', { fileId, uuid });
      return;
    }
    try {
      await updatePoiAttachmentCaption(selectedPoiInfo.id, uuid, fileId, updatedCaption);
    } catch (error) {
      console.error('Error updating image caption:', error);
      showSnackBar('Failed to update image caption', 'error');
    }
  };

  return (
    <Grid container spacing={{ xl: 3, md: 2 }} px={2} id={`${location}-attachments`}>
      <MediaViewer
        mediaFiles={validAttachments?.filter((file) => !!file)}
        handleDownloadAttachment={handleDownloadAttachment}
        handleRemoveAttachment={handleRemoveAttachment}
      >
        {attachments
          ?.filter((file) => !!file)
          .map((file, index) => (
            <Grid item lg={4} sm={6} key={file.id || file.name || `attachment-${index}`}>
              <FileDisplay
                referenceId={selectedPoiInfo.id}
                collectionName="poi"
                selectedAttachment={file}
                selectedAttachmentIndex={index}
                showRemoveIcon={false}
                enableUpload={true}
                onUploadComplete={onUploadComplete}
                uploadAttachmentHandler={() => handelPoiAttachmentUpload(file.id)}
                imageCaptureHandler={(caption) =>
                  handelPoiImageCaption(caption, file.id, file.uuid)
                }
                location={location}
              />
            </Grid>
          ))}
      </MediaViewer>
    </Grid>
  );
};
